import { useCallback, useState } from 'react';
import type { FieldProps } from './TextField';
import { useFormField } from '../../hooks/useFormField';
import { QuestionWrapper } from '../QuestionWrapper';

interface DatePart {
  value: string;
  maxLength: number;
  valid: boolean;
  dirty: boolean;
}

interface MonthYearFieldState {
  month: DatePart;
  year: DatePart;
}

function createInitialState(answer: unknown): MonthYearFieldState {
  const state: MonthYearFieldState = {
    month: { value: '', maxLength: 2, valid: false, dirty: false },
    year: { value: '', maxLength: 4, valid: false, dirty: false },
  };

  if (answer) {
    const parts = String(answer).split('/');
    state.month.value = parts[0] || '';
    state.year.value = parts[1] || '';
    state.month.valid = !!state.month.value;
    state.year.valid = !!state.year.value;
  }

  return state;
}

function validatePart(value: string, maxLength: number): { validatedResult: boolean; autoCompletedValue: string } {
  let autoCompletedValue = value;
  let validatedResult = !!value && !isNaN(Number(value)) && value.length === maxLength;

  if (maxLength === 2) {
    validatedResult = !!value && !isNaN(Number(value));
    autoCompletedValue = value.length === 1 ? `0${value}` : value;
  }

  return { validatedResult, autoCompletedValue };
}

export function MonthYearField({ fieldSchema, group }: FieldProps) {
  const field = useFormField(fieldSchema, group);
  const [answerDateField, setAnswerDateField] = useState(() => createInitialState(field.answer));

  const updateAnswer = useCallback(
    (next: MonthYearFieldState) => {
      if (next.month.valid && next.year.valid) {
        field.setAnswer(`${next.month.value}/${next.year.value}`);
      }
    },
    [field]
  );

  const validateValue = (item: 'month' | 'year') => {
    const part = answerDateField[item];
    const { validatedResult, autoCompletedValue } = validatePart(part.value, part.maxLength);

    const next = {
      ...answerDateField,
      [item]: {
        ...part,
        value: autoCompletedValue,
        valid: validatedResult,
        dirty: true,
      },
    };

    setAnswerDateField(next);
    updateAnswer(next);
  };

  const setPartValue = (item: 'month' | 'year', value: string) => {
    setAnswerDateField((prev) => ({
      ...prev,
      [item]: { ...prev[item], value },
    }));
  };

  const showValidationMessage =
    (answerDateField.month.dirty && !answerDateField.month.valid) ||
    (answerDateField.year.dirty && !answerDateField.year.valid);

  return (
    <QuestionWrapper
      {...field}
      fieldSchema={fieldSchema}
      useLegend
      extraValidation={
        showValidationMessage ? (
          <div className="validation-msg active">Please enter a valid date (mm/yyyy).</div>
        ) : undefined
      }
    >
      <div className="answer-group">
        <div className="answer field date flex" id={`${field.question.id}-monthyear`}>
          <label className="small-label right-margin-3 flex-basis--short-input">
            Month
            <input
              type="text"
              className={`month text full-width${answerDateField.month.dirty && !answerDateField.month.valid ? ' invalid' : ''}`}
              onBlur={() => validateValue('month')}
              value={answerDateField.month.value}
              onChange={(e) => setPartValue('month', e.target.value)}
              name={`${field.question.id}--month`}
              placeholder="MM"
              maxLength={answerDateField.month.maxLength}
            />
          </label>
          <label className="small-label right-margin-3 flex-basis--short-input">
            Year
            <input
              type="text"
              className={`year text full-width${answerDateField.year.dirty && !answerDateField.year.valid ? ' invalid' : ''}`}
              onBlur={() => validateValue('year')}
              value={answerDateField.year.value}
              onChange={(e) => setPartValue('year', e.target.value)}
              name={`${field.question.id}--year`}
              placeholder="YYYY"
              maxLength={answerDateField.year.maxLength}
            />
          </label>
        </div>
      </div>
    </QuestionWrapper>
  );
}
